import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Search, Check } from "lucide-react";

// Sélecteur recherchable (pays / fuseaux) — accessible, responsive, conforme à la charte Junago.
// options: [{ value, label, sublabel?, group? }]. Les groupes sont rendus dans l'ordre d'apparition.
export default function SearchSelect({ value, onChange, options, placeholder = "Sélectionner…", searchPlaceholder = "Rechercher…", testid, emptyLabel = "Aucun résultat" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.sublabel || "").toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-10 px-3 inline-flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white text-sm text-left hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-colors"
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span className={`truncate ${selected ? "text-slate-900" : "text-slate-400"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1.5 w-full rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 overflow-hidden"
          data-testid={testid ? `${testid}-panel` : undefined}
        >
          <div className="flex items-center gap-2 px-3 h-10 border-b border-slate-100">
            <Search className="h-4 w-4 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-full bg-transparent text-sm outline-none placeholder:text-slate-400"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid={testid ? `${testid}-empty` : undefined}>
                {emptyLabel}
              </div>
            )}
            {filtered.map((o, i) => {
              const showGroup = o.group && (i === 0 || filtered[i - 1].group !== o.group);
              return (
                <React.Fragment key={`${o.value}-${o.group || ""}`}>
                  {showGroup && (
                    <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{o.group}</div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-violet-50 transition-colors ${o.value === value ? "bg-violet-50/60" : ""}`}
                    data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-slate-800">{o.label}</span>
                      {o.sublabel && o.sublabel !== o.label && (
                        <span className="block truncate text-[11px] text-slate-400">{o.sublabel}</span>
                      )}
                    </span>
                    {o.value === value && <Check className="h-4 w-4 shrink-0 text-violet-600" />}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
